import { createBrowserRouter } from 'react-router-dom';

import { Functionality } from 'api/userPermissions/userPermissions.enum';
import { App } from 'app/App';
import { Announcements } from 'app/announcements/Announcements';
import { AnnouncementAssignedOwnersContainer } from 'app/announcements/announcementAssignedOwners/AnnouncementAssignedOwnersContainer';
import { CardsContainer } from 'app/cards/CardsContainer';
import { CardHistoryContainer } from 'app/cards/cardHistory/CardHistoryContainer';
import { Control } from 'app/control/Control';
import { InstantControlTerminalsContainer } from 'app/control/instantControl/instantControlTerminals/InstantControlTerminalsContainer';
import { RandomControlTerminalsContainer } from 'app/control/randomControl/randomControlTerminals/RandomControlTerminalsContainer';
import { DefinitionsContainer } from 'app/definitions/DefinitionsContainer';
import { DisplayContainer } from 'app/definitions/display/DisplayContainer';
import { AddEventExportFormatContainer } from 'app/definitions/eventsExportFormats/addEventExportFormat/AddEventExportFormatContainer';
import { LogotypeContainer } from 'app/definitions/logotype/LogotypeContainer';
import { OwnersControlContainer } from 'app/definitions/ownersControl/OwnersControlContainer';
import { DevicesContainer } from 'app/devices/DevicesContainer';
import { DodContainer } from 'app/dod/DodContainer';
import { DodMetricsRedirect } from 'app/dod/components/tabRoutes/DodMetricsRedirect';
import { DlqRoute } from 'app/dod/components/tabRoutes/DlqRoute';
import { HistoryRoute } from 'app/dod/components/tabRoutes/HistoryRoute';
import { OverviewRoute } from 'app/dod/components/tabRoutes/OverviewRoute';
import { QueuesRoute } from 'app/dod/components/tabRoutes/QueuesRoute';
import { RetriesRoute } from 'app/dod/components/tabRoutes/RetriesRoute';
import { WorkerRoute } from 'app/dod/components/tabRoutes/WorkerRoute';
import { EventsContainer } from 'app/events/EventsContainer';
import { CurrentPresenceContainer } from 'app/events/currentPresence/CurrentPresenceContainer';
import { CurrentPresenceOwnersContainer } from 'app/events/currentPresence/currentPresenceOwners/CurrentPresenceOwnersContainer';
import { InneEventsContainer } from 'app/events/inneEvents/InneEventsContainer';
import { GlcContainer } from 'app/glc/GlcContainer';
import { GlcHistoryContainer } from 'app/glc/glcHistory/GlcHistoryContainer';
import { GlcTerminalsContainer } from 'app/glc/glcTerminals/GlcTerminalsContainer';
import { GroupsContainer } from 'app/groups/GroupsContainer';
import { GroupAssignedOwnersContainer } from 'app/groups/groupAssignedOwners/GroupAssignedOwnersContainer';
import { GroupGlcPermissionsContainer } from 'app/groups/groupGlcPermissions/GroupGlcPermissionsContainer';
import { GroupHistoryContainer } from 'app/groups/groupHistory/GroupHistoryContainer';
import { GroupTerminalsPermissionsContainer } from 'app/groups/groupTerminalsPermissions/GroupTerminalsPermissionsContainer';
import { LoginContainer } from 'app/login/LoginContainer';
import { Navbar } from 'app/navbar/Navbar';
import { OwnersContainer } from 'app/owners/OwnersContainer';
import { OwnerHistory } from 'app/owners/ownerHistory/OwnerHistory';
import { OwnerPermissionsContainer } from 'app/owners/ownerPermissions/OwnerPermissionsContainer';
import { ProfileContainer } from 'app/profile/ProfileContainer';
import { Reports } from 'app/reports/Reports';
import { Schedules } from 'app/schedules/Schedules';
import { ExceptionTerminalsContainer } from 'app/schedules/exceptionTerminals/ExceptionTerminalsContainer';
import { ScheduleDetailsContainer } from 'app/schedules/scheduleDetails/ScheduleDetailsContainer';
import { ScheduleTerminalsContainer } from 'app/schedules/schedulesTerminals/ScheduleTerminalsContainer';
import { Sequences } from 'app/sequences/Sequences';
import { Users } from 'app/users/Users';
import { UserHistory } from 'app/users/userDetails/userHistory/UserHistory';
import { UserPermissionsContainer } from 'app/users/userPermissions/UserPermissionsContainer';
import { UsersLoginHistoryContainer } from 'app/users/usersLoginHistory/UsersLoginHistoryContainer';
import { Wanted } from 'app/wanted/Wanted';
import { WantedTerminalsContainer } from 'app/wanted/wantedTerminals/WantedTerminalsContainer';
import { isDodMonitorEnabled } from 'core/featureFlags';
import { AuthorizedRoute } from 'routing/authorizedRoute/AuthorizedRoute';

import { AppRoute, AppRouteWithParams } from './AppRoute.enum';
import { FeatureRoute } from './featureRoute/FeatureRoute';
import { MultiPermissionsRoute } from './multiPermissionsRoute/MultiPermissionsRoute';
import { PermissionsRoute } from './permissionsRoute/PermissionsRoute';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        index: true,
        element: <LoginContainer />,
      },
      {
        path: '*',
        element: <LoginContainer />,
      },
      {
        path: 'login',
        element: <LoginContainer />,
      },
      {
        path: 'main/',
        element: (
          <AuthorizedRoute>
            <Navbar />
          </AuthorizedRoute>
        ),
        children: [
          {
            path: AppRoute.devices,
            element: (
              <PermissionsRoute permission={Functionality.URZADZENIA_GLC}>
                <DevicesContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.cards,
            element: (
              <PermissionsRoute permission={Functionality.KARTY_POSIADACZE}>
                <CardsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.cardsArchived,
            element: (
              <PermissionsRoute permission={Functionality.ARCHIWUM}>
                <CardsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.cardHistory.path,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <CardHistoryContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.owners,
            element: (
              <PermissionsRoute permission={Functionality.KARTY_POSIADACZE}>
                <OwnersContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.ownersArchived,
            element: (
              <PermissionsRoute permission={Functionality.ARCHIWUM}>
                <OwnersContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.ownerHistory.path,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <OwnerHistory />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.ownerPermissionsEffectiveHistory.path,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <OwnerHistory />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.ownerPermissionsEffective.path,
            element: <OwnerPermissionsContainer />,
          },
          {
            path: AppRouteWithParams.ownerPermissionsIndividual.path,
            element: (
              <PermissionsRoute permission={Functionality.UPR_INDYWIDUALNE}>
                <OwnerPermissionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.ownerPermissionsGroup.path,
            element: <OwnerPermissionsContainer />,
          },
          {
            path: AppRoute.controlRandom,
            element: <Control />,
          },
          {
            path: AppRoute.controlInstant,
            element: <Control />,
          },
          {
            path: AppRouteWithParams.controlInstantReaders.path,
            element: (
              <PermissionsRoute permission={Functionality.KONTROLA}>
                <InstantControlTerminalsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.controlRandomReaders.path,
            element: (
              <PermissionsRoute permission={Functionality.KONTROLA}>
                <RandomControlTerminalsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.controlHistory,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <Control />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.wanted,
            element: (
              <PermissionsRoute permission={Functionality.POSZUKIWANY}>
                <Wanted />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.wantedHistory,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <Wanted />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.announcements,
            element: (
              <PermissionsRoute permission={Functionality.KOMUNIKAT}>
                <Announcements />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.announcementsHistory,
            element: (
              <PermissionsRoute permission={Functionality.KOMUNIKAT}>
                <Announcements />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.announcementOwners.path,
            element: <AnnouncementAssignedOwnersContainer />,
          },
          {
            path: AppRouteWithParams.wantedReaders.path,
            element: <WantedTerminalsContainer />,
          },
          {
            path: AppRoute.events,
            element: (
              <PermissionsRoute permission={Functionality.ZDARZENIA}>
                <EventsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.eventsHistory,
            element: (
              <PermissionsRoute permission={Functionality.ZDARZENIA}>
                <EventsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.eventsOther,
            element: (
              <PermissionsRoute permission={Functionality.ZDARZENIA_INNE}>
                <InneEventsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.eventsCurrentPresence,
            element: (
              <PermissionsRoute permission={Functionality.OBECNOSCI}>
                <CurrentPresenceContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.eventsCurrentPresenceOwners.path,
            element: (
              <PermissionsRoute permission={Functionality.OBECNOSCI}>
                <CurrentPresenceOwnersContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.schedules,
            element: (
              <PermissionsRoute permission={Functionality.HARMONOGRAMY}>
                <Schedules />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.schedulesExceptions,
            element: (
              <PermissionsRoute permission={Functionality.HARMONOGRAMY}>
                <Schedules />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.schedulesHistory,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <Schedules />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.scheduleReaders.path,
            element: <ScheduleTerminalsContainer />,
          },
          {
            path: AppRouteWithParams.scheduleDetails.path,
            element: <ScheduleDetailsContainer />,
          },
          {
            path: AppRouteWithParams.exceptionReaders.path,
            element: <ExceptionTerminalsContainer />,
          },
          {
            path: AppRoute.sequences,
            element: (
              <PermissionsRoute permission={Functionality.SEKWENCJA}>
                <Sequences />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.sequencesHistory,
            element: (
              <PermissionsRoute permission={Functionality.SEKWENCJA}>
                <Sequences />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.definitions,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.positions,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.eventsExportFormats,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.sequencesTerminals,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.ownersControl,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.display,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.logotype,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.passwordPolicy,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.mailAccount,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.automation,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.zones,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.passages,
            element: (
              <PermissionsRoute permission={Functionality.DEFINICJE}>
                <DefinitionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.ownersControl,
            element: <OwnersControlContainer />,
          },
          {
            path: AppRouteWithParams.eventsExportFormats.path,
            element: <AddEventExportFormatContainer />,
          },
          {
            path: AppRoute.display,
            element: <DisplayContainer />,
          },
          {
            path: AppRoute.logotype,
            element: <LogotypeContainer />,
          },
          {
            path: AppRoute.groups,
            element: (
              <PermissionsRoute permission={Functionality.UPR_GRUPOWE}>
                <GroupsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.groupsHistory.path,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <GroupHistoryContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.groupsPermissionsHistory.path,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <GroupHistoryContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.groupsArchived,
            element: (
              <PermissionsRoute permission={Functionality.ARCHIWUM}>
                <GroupsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.groupReadersPermissions.path,
            element: (
              <PermissionsRoute permission={Functionality.UPR_GRUPOWE}>
                <GroupTerminalsPermissionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.groupGlcPermissions.path,
            element: (
              <PermissionsRoute permission={Functionality.UPR_GRUPOWE}>
                <GroupGlcPermissionsContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.groupOwners.path,
            element: (
              <PermissionsRoute permission={Functionality.UPR_GRUPOWE}>
                <GroupAssignedOwnersContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.glc,
            element: (
              <PermissionsRoute permission={Functionality.URZADZENIA_GLC}>
                <GlcContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.glcArchived,
            element: (
              <PermissionsRoute permission={Functionality.ARCHIWUM}>
                <GlcContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.glcHistory.path,
            element: (
              <PermissionsRoute permission={Functionality.URZADZENIA_GLC}>
                <GlcHistoryContainer />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.glcTerminals.path,
            element: (
              <PermissionsRoute permission={Functionality.URZADZENIA_GLC}>
                <GlcTerminalsContainer />
              </PermissionsRoute>
            ),
          },
          {
            // The DOD monitor is an operator-facing diagnostic surface that must be absent from
            // production builds (REACT_APP_DOD_MONITOR=false in .env.dist.prod). Hiding the navbar
            // entry alone is not enough - without FeatureRoute the tabs stay reachable by typing
            // /main/dod/queues directly.
            //
            // The parent is a LAYOUT route: DodContainer renders the TopBar + Grafana bar once and
            // the active tab through <Outlet />, so switching tabs no longer remounts the socket
            // subscription. Every child carries its own FeatureRoute; nesting the guard twice is
            // harmless and keeps featureFlags.integration.test's count/guard parity honest.
            path: AppRoute.dod,
            element: (
              <FeatureRoute isEnabled={isDodMonitorEnabled()}>
                <AuthorizedRoute adminRoute>
                  <DodContainer />
                </AuthorizedRoute>
              </FeatureRoute>
            ),
            children: [
              {
                path: AppRoute.dod,
                element: (
                  <FeatureRoute isEnabled={isDodMonitorEnabled()}>
                    <AuthorizedRoute adminRoute>
                      <OverviewRoute />
                    </AuthorizedRoute>
                  </FeatureRoute>
                ),
              },
              {
                path: AppRoute.dodQueues,
                element: (
                  <FeatureRoute isEnabled={isDodMonitorEnabled()}>
                    <AuthorizedRoute adminRoute>
                      <QueuesRoute />
                    </AuthorizedRoute>
                  </FeatureRoute>
                ),
              },
              {
                path: AppRoute.dodDlq,
                element: (
                  <FeatureRoute isEnabled={isDodMonitorEnabled()}>
                    <AuthorizedRoute adminRoute>
                      <DlqRoute />
                    </AuthorizedRoute>
                  </FeatureRoute>
                ),
              },
              {
                path: AppRoute.dodRetries,
                element: (
                  <FeatureRoute isEnabled={isDodMonitorEnabled()}>
                    <AuthorizedRoute adminRoute>
                      <RetriesRoute />
                    </AuthorizedRoute>
                  </FeatureRoute>
                ),
              },
              {
                path: AppRoute.dodWorker,
                element: (
                  <FeatureRoute isEnabled={isDodMonitorEnabled()}>
                    <AuthorizedRoute adminRoute>
                      <WorkerRoute />
                    </AuthorizedRoute>
                  </FeatureRoute>
                ),
              },
              {
                path: AppRoute.dodHistory,
                element: (
                  <FeatureRoute isEnabled={isDodMonitorEnabled()}>
                    <AuthorizedRoute adminRoute>
                      <HistoryRoute />
                    </AuthorizedRoute>
                  </FeatureRoute>
                ),
              },
              {
                // v1 shipped "Metryki" at /main/dod/metrics; v2 splits it into Przeglad and
                // Retransmisje. Bookmarks and the docs keep working — including their query
                // string, which carries the range and filters a bookmarked link exists for.
                path: AppRoute.dodMetrics,
                element: (
                  <FeatureRoute isEnabled={isDodMonitorEnabled()}>
                    <DodMetricsRedirect />
                  </FeatureRoute>
                ),
              },
            ],
          },
          {
            path: AppRoute.users,
            element: (
              <AuthorizedRoute adminRoute>
                <PermissionsRoute permission={Functionality.UZYTKOWNICY}>
                  <Users />
                </PermissionsRoute>
              </AuthorizedRoute>
            ),
            children: [
              {
                path: AppRoute.usersLoginHistory,
                element: (
                  <PermissionsRoute permission={Functionality.HISTORIA}>
                    <UsersLoginHistoryContainer />
                  </PermissionsRoute>
                ),
              },
            ],
          },
          {
            path: AppRouteWithParams.userHistory.path,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <UserHistory />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRouteWithParams.userPermissionsHistory.path,
            element: (
              <PermissionsRoute permission={Functionality.HISTORIA}>
                <UserHistory />
              </PermissionsRoute>
            ),
          },
          {
            path: AppRoute.profile,
            element: <ProfileContainer />,
          },
          {
            path: AppRouteWithParams.userDataPermissions.path,
            element: <UserPermissionsContainer />,
          },
          {
            path: AppRouteWithParams.userFunctionalPermissions.path,
            element: <UserPermissionsContainer />,
          },
          {
            path: AppRoute.reports,
            element: (
              <MultiPermissionsRoute
                permissions={[
                  Functionality.ZESTAWIENIE_UPRAWNIEN,
                  Functionality.RAPORT_WYDANYCH_KART,
                  Functionality.RAPORT_POSIADACZY_BEZ_ZDARZEN,
                  Functionality.RAPORT_ZDARZEN,
                  Functionality.RAPORT_ZDARZEN_INNE,
                  Functionality.RAPORT_CZASU_OBECNOSCI,
                  Functionality.RAPORT_OSOB_OBECNYCH,
                ]}
              >
                <Reports />
              </MultiPermissionsRoute>
            ),
          },
        ],
      },
    ],
  },
]);
